import { useState, useEffect, useCallback } from 'react';
import {
  Image as ImageIcon, Upload, Search, RefreshCw, X, Loader2, Check, Trash2, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { mediaApi } from '../lib/mediaApi';

const IMAGE_TYPES  = ['uploaded', 'stock', 'ai_generated', 'logo', 'licensed'];
const ORIENTATIONS = ['landscape', 'portrait', 'square'];
const EMPTY_FILTERS = { search: '', image_type: '', orientation: '', tags: '' };

// Neutral inline placeholder for broken/missing images
const FALLBACK_IMG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='225'%3E%3Crect width='400' height='225' fill='%23f1f5f9'/%3E%3C/svg%3E";

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function TypeBadge({ type }) {
  if (!type) return null;
  return (
    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-white/90 text-slate-600 rounded border border-slate-200 font-semibold">
      {type.replace('_', ' ')}
    </span>
  );
}

function AssetCard({ asset, onSelect, selected }) {
  return (
    <div
      onClick={() => onSelect(asset)}
      className={`relative group cursor-pointer rounded-xl overflow-hidden border transition-all ${
        selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200 hover:border-blue-300'
      }`}
    >
      <div className="aspect-video bg-slate-100 relative">
        <img
          src={asset.thumbnail_url || asset.file_url}
          alt={asset.alt_text || asset.filename}
          className="w-full h-full object-cover"
          onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
        />
        <div className="absolute top-2 left-2"><TypeBadge type={asset.image_type} /></div>
      </div>
      <div className="p-2 bg-white">
        <p className="text-xs font-medium text-slate-900 truncate">{asset.filename}</p>
        <p className="text-xs text-slate-400 mt-1">Used {asset.usage_count || 0}x</p>
        {asset.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {asset.tags.slice(0, 3).map(tag => (
              <span key={tag} className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-500">{tag}</span>
            ))}
            {asset.tags.length > 3 && <span className="text-[10px] text-slate-400">+{asset.tags.length - 3}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function LibraryTab() {
  const [assets, setAssets]     = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(false);
  const [selected, setSelected] = useState(null);
  const [offset, setOffset]     = useState(0);
  const [filters, setFilters]   = useState({ ...EMPTY_FILTERS });
  const limit = 24;

  const debouncedSearch = useDebounce(filters.search, 350);
  const debouncedTags   = useDebounce(filters.tags, 350);
  const searchPending   = filters.search !== debouncedSearch;
  const tagsPending     = filters.tags !== debouncedTags;

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, offset };
      if (debouncedSearch)     params.search      = debouncedSearch;
      if (filters.image_type)  params.image_type  = filters.image_type;
      if (filters.orientation) params.orientation = filters.orientation;
      if (debouncedTags)       params.tags        = debouncedTags;
      const res = await mediaApi.getAssets(params);
      setAssets(res.data.assets);
      setTotal(res.data.total);
    } catch {
      toast.error('Failed to load media library');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, debouncedTags, filters.image_type, filters.orientation, offset]);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  const setFilter = (key, value) => {
    setOffset(0);
    setFilters(f => ({ ...f, [key]: value }));
  };

  const hasActiveFilters = filters.search || filters.tags || filters.image_type || filters.orientation;
  const totalPages = Math.ceil(total / limit);
  const page       = Math.floor(offset / limit) + 1;

  return (
    <div className="space-y-4">
      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="w-full pl-8 pr-8 py-2 border border-slate-200 rounded-lg text-sm bg-white text-slate-900 placeholder:text-slate-400"
            placeholder="Search by filename, alt text, caption..."
            value={filters.search}
            onChange={e => setFilter('search', e.target.value)}
          />
          {(searchPending || (loading && filters.search)) && (
            <Loader2 size={13} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />
          )}
        </div>

        <div className="relative min-w-40">
          <input
            className="w-full pl-3 pr-8 py-2 border border-slate-200 rounded-lg text-sm bg-white text-slate-900 placeholder:text-slate-400"
            placeholder="Tags — e.g. finance, Brent"
            value={filters.tags}
            onChange={e => setFilter('tags', e.target.value)}
          />
          {(tagsPending || (loading && filters.tags)) && (
            <Loader2 size={13} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />
          )}
        </div>

        <select
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-slate-900"
          value={filters.image_type}
          onChange={e => setFilter('image_type', e.target.value)}
        >
          <option value="">All types</option>
          {IMAGE_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
        </select>

        <select
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-slate-900"
          value={filters.orientation}
          onChange={e => setFilter('orientation', e.target.value)}
        >
          <option value="">All orientations</option>
          {ORIENTATIONS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>

        <button
          onClick={fetchAssets}
          className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600"
          title="Refresh"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>

        {hasActiveFilters && (
          <button
            onClick={() => { setOffset(0); setFilters({ ...EMPTY_FILTERS }); }}
            className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-500 hover:bg-slate-50 hover:text-slate-900"
          >
            <X size={13} /> Clear
          </button>
        )}
      </div>

      <p className="text-sm text-slate-400 flex items-center gap-2">
        {total} asset{total !== 1 ? 's' : ''}
        {loading && <Loader2 size={13} className="animate-spin" />}
      </p>

      {loading && assets.length === 0 ? (
        <div className="flex items-center justify-center py-16 gap-2 text-slate-400 text-sm">
          <Loader2 size={16} className="animate-spin" /> Loading assets...
        </div>
      ) : assets.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">No assets found.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {assets.map(asset => (
            <AssetCard
              key={asset.id}
              asset={asset}
              selected={selected?.id === asset.id}
              onSelect={setSelected}
            />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center gap-2 justify-center pt-4">
          <button
            disabled={page === 1}
            onClick={() => setOffset(o => Math.max(0, o - limit))}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-700 disabled:opacity-40 hover:bg-slate-50"
          >
            Previous
          </button>
          <span className="text-sm text-slate-400">{page} of {totalPages}</span>
          <button
            disabled={page === totalPages}
            onClick={() => setOffset(o => o + limit)}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-700 disabled:opacity-40 hover:bg-slate-50"
          >
            Next
          </button>
        </div>
      )}

      {selected && (
        <AssetDrawer
          asset={selected}
          onClose={() => setSelected(null)}
          onUpdate={fetchAssets}
          onDelete={() => { fetchAssets(); setSelected(null); }}
        />
      )}
    </div>
  );
}

function AssetDrawer({ asset, onClose, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [form, setForm] = useState({
    alt_text:      asset.alt_text || '',
    caption:       asset.caption || '',
    accreditation: asset.source_attribution || '',
    tags:          asset.tags?.join(', ') || '',
    location:      asset.location || '',
    image_type:    asset.image_type || 'uploaded',
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await mediaApi.updateAsset(asset.id, {
        alt_text:           form.alt_text,
        caption:            form.caption,
        source_attribution: form.accreditation,
        tags:               form.tags.split(',').map(t => t.trim()).filter(Boolean),
        location:           form.location,
        image_type:         form.image_type,
      });
      toast.success('Asset updated');
      onUpdate();
      setEditing(false);
    } catch {
      toast.error('Failed to update asset');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Deactivate this asset? It will no longer appear in the library.')) return;
    try {
      await mediaApi.deleteAsset(asset.id);
      toast.success('Asset deactivated');
      onDelete();
    } catch {
      toast.error('Failed to deactivate asset');
    }
  };

  const labelCls = 'text-xs font-medium text-slate-600 mb-1 block';
  const inputCls = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-slate-900';

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl border-l border-slate-200 p-6 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Asset Details</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
        </div>

        <img
          src={asset.file_url}
          alt={asset.alt_text || asset.filename}
          className="w-full rounded-lg border border-slate-200 object-cover max-h-64"
          onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
        />

        <div className="text-sm text-slate-500 space-y-1">
          <p><span className="font-medium text-slate-900">File:</span> {asset.filename}</p>
          {asset.width > 0 && <p><span className="font-medium text-slate-900">Size:</span> {asset.width}×{asset.height}px</p>}
          <p><span className="font-medium text-slate-900">Orientation:</span> {asset.orientation || '—'}</p>
          <p><span className="font-medium text-slate-900">Type:</span> {asset.image_type?.replace('_', ' ')}</p>
          <p><span className="font-medium text-slate-900">Used:</span> {asset.usage_count || 0} time{asset.usage_count !== 1 ? 's' : ''}</p>
        </div>

        {editing ? (
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Type</label>
              <select
                className={inputCls}
                value={form.image_type}
                onChange={e => setForm(f => ({ ...f, image_type: e.target.value }))}
              >
                {IMAGE_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Alt text</label>
              <input className={inputCls} value={form.alt_text} onChange={e => setForm(f => ({ ...f, alt_text: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Caption</label>
              <input className={inputCls} value={form.caption} onChange={e => setForm(f => ({ ...f, caption: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Accreditation</label>
              <input className={inputCls} value={form.accreditation} onChange={e => setForm(f => ({ ...f, accreditation: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Tags (comma separated)</label>
              <input className={inputCls} value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Location</label>
              <input className={inputCls} value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />
            </div>
            <div className="flex gap-2 pt-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-40"
              >
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Save
              </button>
              <button
                onClick={() => setEditing(false)}
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-slate-500 space-y-1">
              <p><span className="font-medium text-slate-900">Alt text:</span> {asset.alt_text || '—'}</p>
              <p><span className="font-medium text-slate-900">Caption:</span> {asset.caption || '—'}</p>
              <p><span className="font-medium text-slate-900">Accreditation:</span> {asset.source_attribution || '—'}</p>
              <p><span className="font-medium text-slate-900">Location:</span> {asset.location || '—'}</p>
              {asset.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {asset.tags.map(tag => (
                    <span key={tag} className="text-xs bg-slate-100 px-1.5 py-0.5 rounded text-slate-500">{tag}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setEditing(true)}
                className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 hover:bg-slate-50"
              >
                <Pencil size={13} /> Edit
              </button>
              <button
                onClick={handleDelete}
                className="flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 rounded-lg text-sm hover:bg-red-50"
              >
                <Trash2 size={13} /> Delete
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function UploadTab({ onUploaded }) {
  const [files, setFiles]         = useState([]);
  const [perFile, setPerFile]     = useState([]);
  const [dragging, setDragging]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    image_type:    'uploaded',
    tags:          '',
    alt_text:      '',
    location:      '',
    accreditation: '',
  });

  const addFiles = (newFiles) => {
    const imgs = newFiles.filter(f => f.type.startsWith('image/'));
    setFiles(prev => [...prev, ...imgs]);
    setPerFile(prev => [...prev, ...imgs.map(() => ({ caption: '' }))]);
  };

  const removeFile = (i) => {
    setFiles(prev => prev.filter((_, j) => j !== i));
    setPerFile(prev => prev.filter((_, j) => j !== i));
  };

  const handleUpload = async () => {
    if (!files.length) return;
    setUploading(true);
    let ok = 0;

    for (let i = 0; i < files.length; i++) {
      const fd = new FormData();
      fd.append('file', files[i]);
      fd.append('image_type', form.image_type);
      fd.append('tags', form.tags);
      fd.append('alt_text', form.alt_text || files[i].name);
      fd.append('location', form.location);
      fd.append('caption', perFile[i]?.caption || '');
      fd.append('source_attribution', form.accreditation || '');
      try {
        await mediaApi.uploadAsset(fd);
        ok += 1;
      } catch {
        toast.error(`Failed to upload ${files[i].name}`);
      }
    }

    setUploading(false);
    if (ok > 0) {
      toast.success(`${ok} file${ok > 1 ? 's' : ''} uploaded`);
      setFiles([]);
      setPerFile([]);
      onUploaded();
    }
  };

  const labelCls = 'text-xs font-medium text-slate-600 mb-1 block';
  const inputCls = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-slate-900';

  return (
    <div className="space-y-5 max-w-2xl">
      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); addFiles(Array.from(e.dataTransfer.files)); }}
        className={`flex flex-col items-center justify-center py-10 border-2 border-dashed rounded-xl transition-colors ${
          dragging ? 'border-blue-400 bg-blue-50' : 'border-slate-200 bg-white'
        }`}
      >
        <Upload size={28} className="text-slate-400 mb-2" />
        <p className="text-sm text-slate-500 mb-2">Drag and drop images here, or</p>
        <label className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 cursor-pointer">
          Browse files
          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={e => addFiles(Array.from(e.target.files))}
          />
        </label>
      </div>

      {/* Selected files */}
      {files.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-900">
            {files.length} file{files.length !== 1 ? 's' : ''} selected
            {files.length > 1 && <span className="text-xs text-slate-400 ml-2 font-normal">— caption per image</span>}
          </p>
          {files.map((file, i) => {
            const previewUrl = URL.createObjectURL(file);
            return (
              <div key={i} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100">
                  <span className="text-sm text-slate-900 flex-1 truncate font-medium">{file.name}</span>
                  <span className="text-xs text-slate-400">{(file.size / 1024).toFixed(0)}KB</span>
                  <button onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-500"><X size={14} /></button>
                </div>
                <div className="flex gap-3 p-3">
                  <img
                    src={previewUrl}
                    alt={file.name}
                    className="w-28 h-20 object-cover rounded-lg shrink-0 border border-slate-200"
                    onLoad={() => URL.revokeObjectURL(previewUrl)}
                  />
                  <div className="flex-1">
                    <label className={labelCls}>Caption</label>
                    <input
                      className={inputCls}
                      placeholder="Image caption"
                      value={perFile[i]?.caption || ''}
                      onChange={e => setPerFile(prev => prev.map((p, j) => j === i ? { ...p, caption: e.target.value } : p))}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Shared metadata */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Type</label>
          <select
            className={inputCls}
            value={form.image_type}
            onChange={e => setForm(f => ({ ...f, image_type: e.target.value }))}
          >
            {IMAGE_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Location</label>
          <input className={inputCls} value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />
        </div>
        <div className="col-span-2">
          <label className={labelCls}>Tags (comma separated)</label>
          <input className={inputCls} placeholder="finance, Brent, council" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} />
        </div>
        <div>
          <label className={labelCls}>Alt text</label>
          <input className={inputCls} value={form.alt_text} onChange={e => setForm(f => ({ ...f, alt_text: e.target.value }))} />
        </div>
        <div>
          <label className={labelCls}>Accreditation</label>
          <input className={inputCls} value={form.accreditation} onChange={e => setForm(f => ({ ...f, accreditation: e.target.value }))} />
        </div>
      </div>

      <button
        onClick={handleUpload}
        disabled={uploading || !files.length}
        className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-40"
      >
        {uploading
          ? <><Loader2 size={14} className="animate-spin" /> Uploading...</>
          : <><Upload size={14} /> Upload {files.length > 0 ? `${files.length} file${files.length > 1 ? 's' : ''}` : ''}</>
        }
      </button>
    </div>
  );
}

export default function MediaPage() {
  const [activeTab, setActiveTab] = useState('library');

  const tabs = [
    { id: 'library', label: 'Library', icon: ImageIcon },
    { id: 'upload',  label: 'Upload',  icon: Upload },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Media Library</h1>
        <p className="text-sm text-slate-400 mt-1">Browse and upload images shared across all publications</p>
      </div>

      <div className="inline-flex gap-1 bg-slate-100 p-1 rounded-lg mb-6">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === id
                ? 'bg-white text-blue-600 shadow-sm border border-slate-200'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'library' && <LibraryTab />}
      {activeTab === 'upload'  && <UploadTab onUploaded={() => setActiveTab('library')} />}
    </div>
  );
}